<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Performance de Agentes no CRM',
  },
});

function formatCurrency(value) {
  if (!value) return 'R$ 0';
  if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `R$ ${(value / 1000).toFixed(1)}K`;
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 0 })}`;
}

function formatNumber(num) {
  if (!num) return '0';
  return num.toLocaleString('pt-BR');
}

function getWinRateColor(rate) {
  if (rate >= 30) return 'text-n-teal-11';
  if (rate >= 15) return 'text-n-amber-11';
  return 'text-n-ruby-11';
}

function getInitials(name) {
  if (!name) return '??';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const sortedAgents = computed(() => {
  return [...props.data].sort(
    (a, b) => (b.total_value || 0) - (a.total_value || 0)
  );
});
</script>

<template>
  <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-users text-lg text-n-blue-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <div
      v-else-if="data.length === 0"
      class="flex items-center justify-center h-48 text-n-slate-11"
    >
      <span class="text-sm">Nenhum agente com negocios</span>
    </div>

    <div v-else class="flex flex-col gap-3">
      <div
        v-for="(agent, idx) in sortedAgents"
        :key="agent.agent_id"
        class="p-4 rounded-lg bg-n-alpha-1"
      >
        <!-- Header -->
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-3">
            <div class="relative">
              <img
                v-if="agent.avatar_url"
                :src="agent.avatar_url"
                :alt="agent.agent_name"
                class="w-10 h-10 rounded-full object-cover"
              />
              <div
                v-else
                class="w-10 h-10 rounded-full bg-n-blue-3 flex items-center justify-center text-n-blue-11 font-medium text-sm"
              >
                {{ getInitials(agent.agent_name) }}
              </div>
              <span
                v-if="idx < 3"
                class="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold"
                :class="{
                  'bg-n-amber-9 text-white': idx === 0,
                  'bg-n-slate-9 text-white': idx === 1,
                  'bg-n-orange-9 text-white': idx === 2,
                }"
              >
                {{ idx + 1 }}
              </span>
            </div>
            <div class="flex flex-col">
              <span class="font-medium text-n-slate-12">{{
                agent.agent_name
              }}</span>
              <span class="text-xs text-n-slate-11">{{ formatNumber(agent.total_deals) }} negocios</span>
            </div>
          </div>
          <div class="text-right">
            <div class="text-lg font-bold text-n-teal-11">
              {{ formatCurrency(agent.total_value) }}
            </div>
            <div class="text-xs text-n-slate-11">Valor Total</div>
          </div>
        </div>

        <!-- Stats Grid -->
        <div class="grid grid-cols-4 gap-2">
          <div class="text-center p-2 rounded bg-n-alpha-2">
            <div class="text-sm font-bold text-n-blue-11">
              {{ formatNumber(agent.deals_open) }}
            </div>
            <div class="text-xs text-n-slate-10">Abertos</div>
          </div>
          <div class="text-center p-2 rounded bg-n-alpha-2">
            <div class="text-sm font-bold text-n-teal-11">
              {{ formatNumber(agent.deals_won) }}
            </div>
            <div class="text-xs text-n-slate-10">Ganhos</div>
          </div>
          <div class="text-center p-2 rounded bg-n-alpha-2">
            <div class="text-sm font-bold text-n-ruby-11">
              {{ formatNumber(agent.deals_lost) }}
            </div>
            <div class="text-xs text-n-slate-10">Perdidos</div>
          </div>
          <div class="text-center p-2 rounded bg-n-alpha-2">
            <div
              class="text-sm font-bold"
              :class="getWinRateColor(agent.win_rate)"
            >
              {{ agent.win_rate?.toFixed(1) || 0 }}%
            </div>
            <div class="text-xs text-n-slate-10">Taxa</div>
          </div>
        </div>

        <!-- Value Breakdown -->
        <div
          class="flex items-center justify-between mt-3 pt-3 border-t border-n-weak text-xs"
        >
          <span class="text-n-slate-11">
            Ganho:
            <span class="text-n-teal-11 font-medium">{{
              formatCurrency(agent.won_value)
            }}</span>
          </span>
          <span class="text-n-slate-11">
            Pipeline:
            <span class="text-n-blue-11 font-medium">{{
              formatCurrency(agent.pipeline_value)
            }}</span>
          </span>
          <span class="text-n-slate-11">
            Ticket:
            <span class="text-n-violet-11 font-medium">{{
              formatCurrency(agent.avg_deal_value)
            }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
